<template>
  <div class="view marketing campaign edit">
    <div class="page" v-if="campaign">
      <a-page-header
        class="rounded bg-gray-1"
        :title="'Campaign Details'"
        :sub-title="`${campaign.name ? campaign.name : ''}`"
        @back="() => $router.push('/marketing/campaigns')"
      >
        <template slot="tags">
          <a-tag :color="'blue'">{{ campaign.notification_channel | CAPITALIZE_ }}</a-tag>
          <a-tag :color="campaign.status | CAMPAIGN_STATUS_COLOR_">{{ campaign.status | CAMPAIGN_STATUS_ }}</a-tag>
        </template>

        <template slot="extra">
          <a-button key="0" type="danger"> Delete </a-button>
          <a-button key="1" type="primary" @click="onSave"> Save </a-button>
          <a-button class="ml-3" key="2" :type="handleButton.type" @click="onHandle" :disabled="handleButton.disabled">
            {{ handleButton.text }}
          </a-button>
        </template>

        <a-descriptions :column="2">
          <a-descriptions-item label="Campaign ID">
            <input class="idbox" :value="campaign._id" />
          </a-descriptions-item>
          <a-descriptions-item label="Created By" v-if="campaign.created_by">
            {{ campaign.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updated by" v-if="campaign.updated_by">
            {{ campaign.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At" v-if="campaign.created_at">
            <span class="badge badge-primary"> {{ campaign.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At" v-if="campaign.updated_at">
            <span class="badge badge-success"> {{ campaign.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>

      <a-tabs v-model="currentTab" @change="onTagChange">
        <!-- Tab:Meta -->
        <a-tab-pane key="setting" tab="Setting">
          <div class="content mt-4">
            <!-- form -->
            <a-form-model
              ref="campaignForm"
              :model="campaign"
              :rules="rules"
              :label-col="labelCol"
              :wrapper-col="wrapperCol"
            >
              <!-- Notification Channel -->
              <a-form-model-item label="Notification Channel" prop="notification_channel">
                <a-radio-group
                  v-model="campaign.notification_channel"
                  default-value="email"
                  button-style="solid"
                  :disabled="channel_fixed"
                >
                  <a-radio-button value="email"> Email </a-radio-button>
                  <a-radio-button value="sms"> SMS </a-radio-button>
                </a-radio-group>
              </a-form-model-item>

              <!-- Sender -->
              <a-form-model-item label="Sender" prop="sender">
                <a-input v-model="campaign.sender" placeholder="Sender of the campaign" />
              </a-form-model-item>

              <!-- Recipients Group -->
              <a-form-model-item label="Recipients Group" prop="recipient_group">
                <a-select v-model="campaign.recipient_group" default-value="all">
                  <a-select-option v-for="group in recipient_groups" :key="group._id" :value="group._id">
                    {{ group.name }} ({{ group.recipients.length }})
                  </a-select-option>
                </a-select>
              </a-form-model-item>

              <!-- Campaign Title -->
              <a-form-model-item label="Campaign Title" prop="title">
                <a-input v-model="campaign.title" placeholder="Title of the campaign" />
              </a-form-model-item>

              <!-- Subtitle -->
              <a-form-model-item label="Subtitle" prop="subtitle">
                <a-input v-model="campaign.subtitle" placeholder="Subtitle" />
              </a-form-model-item>

              <!-- Campaign Content(SMS) -->
              <template v-if="campaign.notification_channel === 'sms'">
                <a-form-model-item label="SMS Content" prop="text">
                  <a-input v-model="campaign.text" type="textarea" :rows="12" placeholder="Campaign content..." />
                </a-form-model-item>
              </template>

              <!-- Preset Template -->
              <template v-if="campaign.notification_channel === 'email'">
                <a-form-model-item label="Preset Template" prop="template">
                  <a-select v-model="campaign.template" default-value="default">
                    <a-select-option value="default"> Default </a-select-option>
                    <a-select-option value="template1"> Template 1 </a-select-option>
                    <a-select-option value="template2"> Template 2 </a-select-option>
                    <a-select-option value="template3"> Template 3 </a-select-option>
                    <a-select-option value="template4"> Template 4 </a-select-option>
                    <a-select-option value="template5"> Template 5 </a-select-option>
                    <a-select-option value="template6"> Template 6 </a-select-option>
                  </a-select>
                </a-form-model-item>

                <!-- Campaign Content(Email) -->
                <a-form-item label="Email Content">
                  <a-button type="primary" @click="currentTab = 'design'" icon="edit">Design</a-button>
                  <a-button class="ml-2" type="primary" @click="currentTab = 'preview'" icon="eye">Preview</a-button>
                </a-form-item>
              </template>

              <a-form-model-item label="Scheduled Date" prop="scheduled">
                <a-date-picker
                  v-model="campaign.scheduled"
                  show-time
                  type="date"
                  placeholder="Pick a scheduled time"
                  style="width: 100%"
                />
              </a-form-model-item>

              <a-form-model-item label="Description" prop="description">
                <a-input
                  v-model="campaign.description"
                  type="textarea"
                  :rows="12"
                  placeholder="Campaign description..."
                />
              </a-form-model-item>

              <!-- actions -->
              <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
                <a-button type="primary" @click="onSave"> Update </a-button>
              </a-form-model-item>
            </a-form-model>
          </div>
        </a-tab-pane>

        <!-- Tab:Design -->
        <a-tab-pane key="design" tab="Email Design" v-if="campaign.notification_channel === 'email'">
          <div class="design" v-if="currentTab === 'design'">
            <EmailBuilder :design="campaign.design" @onAutoSaving="onAutoSaving" />
          </div>
        </a-tab-pane>

        <!-- Tab:Preview -->
        <a-tab-pane key="preview" tab="Email Preview" forceRender v-if="campaign.notification_channel === 'email'">
          <div class="preview">
            <div class="inner">
              <div v-html="campaign.html"></div>
            </div>
          </div>
        </a-tab-pane>

        <!-- Tab:Feedback -->
        <a-tab-pane key="feedback" tab="Campaign Results" v-if="campaign.status === 'sent'">
          <div class="feedback">
            <div class="inner">
              {{ campaign.finished_at }}
              {{ campaign.rejected_recipients }}
              {{ campaign.accepted_recipients }}
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
import { EmailBuilder } from '@/components/reno/general'
import store from '@/store'
const KEY = 'campaign'

export default {
  components: {
    EmailBuilder,
  },
  data() {
    return {
      campaign: null,
      handleButton: {
        text: 'Schedule',
        type: 'primary',
      },
      currentTab: 'setting',
      size: 'large',
      labelCol: { xs: 24, sm: 4 },
      wrapperCol: { xs: 24, sm: 20 },
      rules: {
        notification_channel: [{ required: true, message: 'Please select notification channel', trigger: 'blur' }],
        sender: [{ required: true, message: 'Please input campaign sender', trigger: 'blur' }],
        recipient_group: [{ required: true, message: 'Please select recipient group', trigger: 'blur' }],
        title: [
          { required: true, message: 'Please input campaign title', trigger: 'blur' },
          { min: 3, max: 120, message: 'Length should be 3 to 120', trigger: 'blur' },
        ],
        sender: [{ required: true, message: 'Please input campaign sender', trigger: 'blur' }],
        // description: [{ required: true, message: 'Please input campaign description', trigger: 'blur' }],
      },
      recipient_groups: [],
    }
  },
  computed: {
    channel_fixed() {
      return this.campaign && (this.campaign.status == 'scheduled' || this.campaign.status == 'sending')
    },
  },
  methods: {
    async fetch() {
      if (this.$route.params && this.$route.params.id) {
        this.campaign = await store.dispatch('marketing/fetchCampaign', this.$route.params.id)
      }
    },
    onTagChange(val) {
      // console.log(val)
    },
    onAutoSaving(data) {
      this.campaign.design = data.design
      this.campaign.html = data.html
    },

    async onSave() {
      this.$refs.campaignForm.validate(async (valid) => {
        if (valid) {
          const campaign = await store.dispatch('marketing/updateCampaign', this.campaign)
          if (campaign) {
            this.campaign = campaign
            this.updateHandleButton()
            this.$message.success(`Campaign ${campaign.title} has been updated successfully!`)
          }
        } else {
          this.$message.error('Oops, The campaign creation failed!')
          return false
        }
      })
    },

    updateHandleButton() {
      switch (this.campaign.status) {
        case 'draft':
          this.handleButton = { text: 'Schedule', type: 'primary', disabled: false }
          break
        case 'scheduled':
          this.handleButton = { text: 'Revoke', type: 'danger', disabled: false }
          break
        case 'sent':
          this.handleButton = { text: 'Completed', type: 'dashed', disabled: true }
          break
      }
    },
    async onHandle() {
      switch (this.campaign.status) {
        case 'draft':
          this.campaign.status = 'scheduled'
          break
        case 'scheduled':
          this.campaign.status = 'draft'
          break
        case 'sending':
          this.campaign.status = 'cancelled'
          break
      }

      await this.onSave()
    },
  },
  async created() {
    await this.fetch()
    this.updateHandleButton()
  },
  async mounted() {
    this.recipient_groups = await this.$store.dispatch('marketing/fetchRecipientGroups')
  },
}
</script>

<style lang="scss" scoped>
.unlayer-editor {
  height: 800px;
}

.preview {
  .inner {
    border: 1px solid #ddd;
    border-radius: 3px;
    padding: 1rem;
  }
}
</style>
